import React, { useCallback, useId, useState } from 'react';
import style from './style.module.scss';

import { joinNames } from '../helpers';
import { isNotification, Notification, NotificationIcon } from '../NotificationIcon';

// eslint-disable-next-line @typescript-eslint/ban-types
type HelpBoxProps = React.PropsWithChildren<{
    expanded?: boolean;
    id: string;
}> &
    React.HTMLAttributes<HTMLDivElement>;

export default function HelpBox({ className, children, expanded, ...attr }: HelpBoxProps): JSX.Element {
    const classNames = joinNames(className, style.box, typeof expanded == 'boolean' ? (expanded ? style.expand : style.collapse) : undefined);
    return (
        <div aria-expanded={expanded} className={classNames} {...attr}>
            {children}
        </div>
    );
}

/**
 *
 * @param text help text
 * @returns Tuple with two controls: [HelpBox, Icon]
 */
export function useHelpBox(text: string | undefined | Notification | JSX.Element | null, name?: string) {
    const id = useId();
    const [showHelpText, setShowHelpText] = useState<boolean | undefined>();

    const helpBox = useCallback(() => {
        const uiTestingProps = name ? { 'data-nameforuitesting': `${name}_helpbox` } : undefined;
        return text ? (
            <HelpBox id={id} expanded={showHelpText} {...uiTestingProps}>
                {typeof text == 'string' ? text : isNotification(text) ? text.title : text}
            </HelpBox>
        ) : (
            <></>
        );
    }, [name, text, id, showHelpText]);
    const icon = useCallback(
        () =>
            text ? (
                <NotificationIcon
                    prefixForUiTesting={name ? `${name}_helpbox` : undefined}
                    aria-controls={id}
                    onClick={() => setShowHelpText(!showHelpText)}
                    question={typeof text == 'string' || !isNotification(text) ? true : text}
                    tabIndex={0}
                    onKeyDown={(e) => e.code == 'Enter' && setShowHelpText(!showHelpText)}
                />
            ) : (
                <></>
            ),
        [id, name, showHelpText, text]
    );

    return [helpBox, icon] as const;
}
